/**
 * MyAgentsShareList: your agents, and for each: who may use it, and who covers the tokens when someone else does. The one place to share,
 * used on the Agents page and behind the Share button in the conversation, so the token choice can never be missed.
 *
 *  - Not shared yet: pick the two choices, then Share. (Defaults are the cautious ones: you approve each use; you cover the tokens.)
 *  - Shared: each choice applies as soon as you change it. Stop sharing withdraws it.
 *  - An agent that uses your private tools or data always runs on your tokens, and the form says why.
 */
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { spacesAPI } from '../../services/api.js';
import { T, btn, Pill, Seg } from './teamUi.jsx';

export const PAYER_NOTICE = 'Whoever uses this agent pays for its model usage, and only after they confirm each run.\n\n'
  + 'The agent\'s instructions will be visible to their model provider (in its logs). Only the request itself is sent, never the team\'s conversation.\n\nContinue?';

const ACCESS = [{ value: 'open', label: 'Anyone, right away', title: 'Teammates can use it at once' }, { value: 'ask', label: 'Only if I approve', title: 'Each teammate asks you first' }];
const payerOptions = (a) => [
  { value: 'owner', label: 'I cover them', title: 'Your keys and budget pay for every run' },
  { value: 'requester', label: 'Whoever uses it', disabled: a.private, title: a.private ? `Always on your tokens: ${a.whyPrivate}` : 'They confirm each run, and their keys pay' },
];

export default function MyAgentsShareList({ spaceId, compact = false, onChanged }) {
  const [agents, setAgents] = useState(null);
  const [draft, setDraft] = useState({});     // agentId -> { access, payer } for agents not shared yet
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setAgents((await spacesAPI.myAgents(spaceId)).agents || []); } catch { setAgents((a) => a || []); }
  }, [spaceId]);
  useEffect(() => { load(); }, [load]);

  const after = async (ok) => {
    toast.success(ok); await load();
    window.dispatchEvent(new Event('lyra:roster-update'));
    if (onChanged) onChanged();
  };
  const act = async (a, fn, ok) => {
    setBusy(a.agentId);
    try { await fn(); await after(ok); }
    catch (e) { toast.error(e?.response?.data?.message || 'Could not change that'); }
    finally { setBusy(null); }
  };
  const choice = (a) => draft[a.agentId] || { access: 'ask', payer: 'owner' };

  if (agents === null) return <div style={{ padding: '14px 20px', fontSize: 12.5, color: T.ink3 }}>Loading your agents…</div>;
  if (!agents.length) return <div style={{ padding: '14px 20px', fontSize: 12.5, color: T.ink3 }}>You have no agents yet. Create one from the Agents page, then share it here.</div>;

  return (
    <div>
      {agents.map((a) => {
        const sh = a.shared && a.shared.status === 'active' ? a.shared : null;
        const blocked = a.shared && a.shared.status === 'blocked';
        const c = sh ? { access: sh.access, payer: sh.payer } : choice(a);
        const set = (patch) => setDraft((d) => ({ ...d, [a.agentId]: { ...choice(a), ...patch } }));
        const changePayer = (v) => {
          if (v === 'requester' && !window.confirm(PAYER_NOTICE)) return;
          if (sh) act(a, () => spacesAPI.setAgentPayer(spaceId, sh.rosterId, v), v === 'requester' ? 'Whoever uses it covers the tokens' : 'You cover the tokens');
          else set({ payer: v });
        };
        return (
          <div key={a.agentId} style={{ padding: compact ? '10px 14px' : '14px 20px', borderTop: `1px solid ${T.border}`, opacity: a.enabled ? 1 : 0.6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <b style={{ fontSize: 13.5, color: T.ink }}>{a.name}</b>
              {sh && <Pill tone="ok">Shared</Pill>}
              {blocked && <Pill tone="bad">Blocked by the team owner</Pill>}
              {!a.enabled && <Pill>Turned off</Pill>}
            </div>
            {a.description && <div style={{ fontSize: 12, color: T.ink3, margin: '3px 0 0' }}>{String(a.description).slice(0, compact ? 80 : 140)}</div>}
            {!blocked && (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap', marginTop: 10 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Who can use it</span>
                  <Seg label="Who can use it" value={c.access} options={ACCESS} disabled={busy === a.agentId}
                    onChange={(v) => (sh ? act(a, () => spacesAPI.setAgentAccess(spaceId, sh.rosterId, v), v === 'open' ? 'Open to everyone' : 'Teammates will ask you first') : set({ access: v }))} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Who covers the tokens</span>
                  <Seg label="Who covers the tokens" value={c.payer} options={payerOptions(a)} disabled={busy === a.agentId} onChange={changePayer} />
                </div>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  {sh
                    ? <button type="button" disabled={busy === a.agentId} style={btn('plain')} onClick={() => act(a, () => spacesAPI.removeAgent(spaceId, sh.rosterId), 'No longer shared')}>Stop sharing</button>
                    : <button type="button" disabled={busy === a.agentId || !a.enabled} style={btn('primary')} onClick={() => act(a, () => spacesAPI.addAgent(spaceId, a.agentId, c.access, c.payer), c.access === 'open' ? 'Shared: open to everyone' : 'Shared: teammates will ask you first')}>Share</button>}
                </span>
              </div>
            )}
            {a.private && !blocked && <div style={{ fontSize: 11.5, color: T.ink4, marginTop: 7 }}>It always runs on your tokens: {a.whyPrivate}.</div>}
          </div>
        );
      })}
    </div>
  );
}
